import type { MutableRefObject } from 'react';
import { useLayoutEffect, useRef } from 'react';

/**
 * Putting a callback inside a ref to be safely called inside the `useEffect`, `useMemo` and `useCallback`.
 *
 * Note: Do not add the `callbackRef.current` into the array dependency. See the example.
 *
 * @param callback The function to be memoized.
 * @returns A ref which holds the callback.
 * @example
 *
 * function useCustomHook(callback) {
 *   const callbackRef = useCallbackRef(callback);
 *   useEffect(() => {
 *     callbackRef.current();
 *     // it's safe to add callbackRef inside the array dependencies
 *     // do not include `callbackRef.current` inside the array dependency
 *   }, [callbackRef]);
 * }
 *
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- The `any` type defines inside the extend part
export const useCallbackRef = <T extends (...args: any[]) => ReturnType<T>>(callback: T): MutableRefObject<T> => {
  const callbackRef = useRef(callback);

  useLayoutEffect(() => {
    callbackRef.current = callback;
  });

  return callbackRef;
};
